/**
 * Utility functions for data compression
 */

/**
 * Compress a base64 string using gzip compression with timeout
 * @param {string} base64String - The base64 string to compress
 * @param {number} timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns {Promise<string>} - Compressed base64 string, or original if compression fails
 */
export const compressBase64 = async (base64String, timeoutMs = 5000) => {
  // Check if compression is supported
  if (typeof CompressionStream === 'undefined') {
    console.warn('CompressionStream not supported, sending uncompressed');
    return base64String;
  }

  try {
    const compressionPromise = new Promise(async (resolve, reject) => {
      try {
        // Convert base64 to bytes
        const bytes = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));

        // Create compression stream
        const compressStream = new CompressionStream('gzip');
        const writer = compressStream.writable.getWriter();
        const reader = compressStream.readable.getReader();

        // Write original data
        await writer.write(bytes);
        await writer.close();

        // Read compressed data
        const chunks = [];
        let result = await reader.read();
        while (!result.done) {
          chunks.push(result.value);
          result = await reader.read();
        }

        // Combine chunks
        const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }

        // Convert back to base64
        resolve(btoa(String.fromCharCode(...combined)));
      } catch (error) {
        reject(error);
      }
    });

    // Race between compression and timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Compression timeout')), timeoutMs);
    });

    return await Promise.race([compressionPromise, timeoutPromise]);

  } catch (error) {
    console.warn(`Compression failed (${error.message}), sending uncompressed`);
    return base64String; // Return original if compression fails or times out
  }
};
